import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import {
  PJInputData,
  PJResultsType,
  ValidationMessages,
} from "../calculadora-pj";
import { calcularPJ } from "../utils/pjFunctions";

// REGRA DE NEGÓCIO: Constante obrigatória
const CALCULATOR_KEY = "calculadora-pj";

const DEFAULT_VALUES: PJInputData = {
  regime: "",
  remuneracaoBruta: "",
  ramoAtividade: "",
  atividadeMEI: "",
  aliquotaPersonalizada: false,
  aliquotaPersonalizadaValor: "",
  proLabore: "",
  numeroDependentes: "0",
} as const;

const parseNumericValue = (value: string): number => {
  return (
    Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0
  );
};

export const PJInputCard = ({
  setResults,
}: {
  setResults: (value: PJResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] = useState<PJInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showMEIAlert, setShowMEIAlert] = useState(false);
  const [showSimplesAlert, setShowSimplesAlert] = useState(false);

  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof PJInputData>(
    field: K,
    value: PJInputData[K],
  ) => {
    setFormData((prev: PJInputData) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (!formData.regime) return ValidationMessages.REGIME_OBRIGATORIO;
    if (!formData.remuneracaoBruta)
      return ValidationMessages.REMUNERACAO_OBRIGATORIA;

    const remuneracaoNum = parseNumericValue(formData.remuneracaoBruta);
    if (remuneracaoNum <= 0) return ValidationMessages.REMUNERACAO_INVALIDA;

    if (formData.regime === "mei" && remuneracaoNum > 6750) {
      return ValidationMessages.LIMITE_MEI_EXCEDIDO;
    }

    if (
      (formData.regime === "simples" || formData.regime === "presumido") &&
      !formData.ramoAtividade
    ) {
      return ValidationMessages.RAMO_ATIVIDADE_OBRIGATORIO;
    }

    return null;
  };

  const executeCalculation = async () => {
    const resultado = calcularPJ(
      formData.regime,
      formData.remuneracaoBruta,
      formData.ramoAtividade,
      formData.atividadeMEI,
      formData.aliquotaPersonalizada,
      formData.aliquotaPersonalizadaValor,
      formData.proLabore,
      formData.numeroDependentes,
    );

    setResults(resultado);
    setMessage("");
  };

  const calcular = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      await executeCalculation();
    } catch (error) {
      console.error("Erro no cálculo:", error);
      setMessage((error as Error).message || ValidationMessages.ERRO_CALCULO);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
    setShowMEIAlert(false);
    setShowSimplesAlert(false);
    setIsCalculating(false);
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      regime: formData.regime,
      remuneracaoBruta: formData.remuneracaoBruta,
      ramoAtividade: formData.ramoAtividade,
      atividadeMEI: formData.atividadeMEI,
      aliquotaPersonalizada: formData.aliquotaPersonalizada,
      aliquotaPersonalizadaValor: formData.aliquotaPersonalizadaValor,
      proLabore: formData.proLabore,
      numeroDependentes: formData.numeroDependentes,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(calcular);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (savedData) {
      setFormData({
        regime: (savedData.regime as string) || DEFAULT_VALUES.regime,
        remuneracaoBruta:
          (savedData.remuneracaoBruta as string) ||
          DEFAULT_VALUES.remuneracaoBruta,
        ramoAtividade:
          (savedData.ramoAtividade as string) || DEFAULT_VALUES.ramoAtividade,
        atividadeMEI:
          (savedData.atividadeMEI as string) || DEFAULT_VALUES.atividadeMEI,
        aliquotaPersonalizada:
          (savedData.aliquotaPersonalizada as boolean) ||
          DEFAULT_VALUES.aliquotaPersonalizada,
        aliquotaPersonalizadaValor:
          (savedData.aliquotaPersonalizadaValor as string) ||
          DEFAULT_VALUES.aliquotaPersonalizadaValor,
        proLabore: (savedData.proLabore as string) || DEFAULT_VALUES.proLabore,
        numeroDependentes:
          (savedData.numeroDependentes as string) ||
          DEFAULT_VALUES.numeroDependentes,
      });
    }
  }, []);

  useEffect(() => {
    if (formData.remuneracaoBruta && formData.regime) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.remuneracaoBruta, formData.regime]);

  return (
    <MainCard>
      <MainCardTitle>Calculadora de Renda PJ</MainCardTitle>
      <form onSubmit={handleSubmit}>
        <MainCardContent>
          {/* Renda Bruta Mensal - Campo Principal */}
          <div>
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="remuneracao-bruta"
            >
              Renda Bruta Mensal *
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="remuneracao-bruta"
                placeholder="0,00"
                type="text"
                value={formData.remuneracaoBruta}
                onChange={(event) =>
                  updateFormField("remuneracaoBruta", sanitizeBrlValue(event))
                }
              />
            </div>
            <small className="text-xs text-gray-600 mt-1 block">
              Total que a empresa receberá mensalmente
            </small>
          </div>

          {/* Regime Tributário - Seção Completa */}
          <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Regime Tributário *
            </h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="regime"
                  value="mei"
                  checked={formData.regime === "mei"}
                  onChange={(e) => updateFormField("regime", e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: "#8d0202" }}
                />
                <span className="text-sm">MEI - até R$ 81.000/ano</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="regime"
                  value="simples"
                  checked={formData.regime === "simples"}
                  onChange={(e) => updateFormField("regime", e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: "#8d0202" }}
                />
                <span className="text-sm">
                  Simples Nacional - até R$ 4.800.000/ano
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="regime"
                  value="presumido"
                  checked={formData.regime === "presumido"}
                  onChange={(e) => updateFormField("regime", e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: "#8d0202" }}
                />
                <span className="text-sm">Lucro Presumido - sem limite</span>
              </label>
            </div>
          </div>

          {/* Alertas */}
          {showMEIAlert && (
            <div className="p-3 bg-red-50 rounded border border-red-200">
              <div className="text-sm text-red-800">
                <strong>Atenção:</strong> Valor excede o limite MEI (R$
                6.750/mês). Considere migrar para o Simples Nacional.
              </div>
            </div>
          )}

          {showSimplesAlert && (
            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-sm text-blue-800">
                <strong>Dica:</strong> Com esse faturamento, você já está na 2ª
                faixa do Simples Nacional. Considere verificar se o Lucro
                Presumido seria mais vantajoso.
              </div>
            </div>
          )}

          {/* Configurações Específicas MEI */}
          {formData.regime === "mei" && (
            <div className="space-y-4">
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="atividade-mei"
                >
                  Tipo de Atividade MEI
                </label>
                <select
                  id="atividade-mei"
                  className="w-full p-2 border rounded"
                  value={formData.atividadeMEI}
                  onChange={(e) =>
                    updateFormField("atividadeMEI", e.target.value)
                  }
                >
                  <option value="">Selecione a atividade</option>
                  <option value="comercio">
                    Comércio/Indústria - R$ 76,90/mês
                  </option>
                  <option value="servicos">
                    Prestação de Serviços - R$ 80,90/mês
                  </option>
                  <option value="comercioServicos">
                    Comércio e Serviços - R$ 81,90/mês
                  </option>
                </select>
              </div>

              {/* Pró-labore (opcional) para MEI */}
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="pro-labore-mei"
                >
                  Pró-labore (opcional)
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="pro-labore-mei"
                    placeholder="0,00"
                    type="text"
                    value={formData.proLabore}
                    onChange={(event) =>
                      updateFormField("proLabore", sanitizeBrlValue(event))
                    }
                  />
                </div>
                <small className="text-xs text-gray-600 mt-1 block">
                  Para MEI não é obrigatório
                </small>
              </div>

              {/* Dependentes para MEI (só se tiver pró-labore) */}
              {parseNumericValue(formData.proLabore) > 0 && (
                <div>
                  <label
                    className="block mb-2 font-medium text-sm"
                    htmlFor="numero-dependentes-mei"
                  >
                    Número de Dependentes
                  </label>
                  <input
                    className="w-full p-2 border rounded"
                    id="numero-dependentes-mei"
                    placeholder="0"
                    type="number"
                    min="0"
                    max="20"
                    value={formData.numeroDependentes}
                    onChange={(event) =>
                      updateFormField("numeroDependentes", event.target.value)
                    }
                  />
                  <small className="text-xs text-gray-600 mt-1 block">
                    Para dedução no IRRF sobre o pró-labore
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Configurações Específicas Simples Nacional */}
          {formData.regime === "simples" && (
            <div className="space-y-4">
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="ramo-atividade"
                >
                  Ramo de Atividade *
                </label>
                <select
                  id="ramo-atividade"
                  className="w-full p-2 border rounded"
                  value={formData.ramoAtividade}
                  onChange={(e) =>
                    updateFormField("ramoAtividade", e.target.value)
                  }
                >
                  <option value="">Selecione o ramo</option>
                  <option value="comercio">Comércio (Anexo I) - 4,0%</option>
                  <option value="industria">Indústria (Anexo II) - 4,5%</option>
                  <option value="servicosGerais">
                    Serviços Gerais (Anexo III) - 6,0%
                  </option>
                  <option value="servicosLimpeza">
                    Serviços Específicos (Anexo IV) - 4,5%
                  </option>
                  <option value="servicosIntelectuais">
                    Serviços Intelectuais (Anexo V) - 15,5%
                  </option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="aliquota-personalizada"
                  checked={formData.aliquotaPersonalizada}
                  onChange={(e) => {
                    updateFormField("aliquotaPersonalizada", e.target.checked);
                    if (!e.target.checked) {
                      updateFormField("aliquotaPersonalizadaValor", "");
                    }
                  }}
                  className="w-4 h-4"
                  style={{ accentColor: "#8d0202" }}
                />
                <label
                  htmlFor="aliquota-personalizada"
                  className="text-sm font-medium"
                >
                  Informar alíquota do DAS manualmente
                </label>
              </div>

              {formData.aliquotaPersonalizada && (
                <div>
                  <label
                    className="block mb-2 font-medium text-sm"
                    htmlFor="aliquota-valor"
                  >
                    Alíquota do Simples (%)
                  </label>
                  <input
                    className="w-full p-2 border rounded"
                    id="aliquota-valor"
                    placeholder="0,00"
                    type="number"
                    min="0"
                    max="33"
                    step="0.01"
                    value={formData.aliquotaPersonalizadaValor}
                    onChange={(event) =>
                      updateFormField(
                        "aliquotaPersonalizadaValor",
                        event.target.value,
                      )
                    }
                  />
                </div>
              )}

              {/* Pró-labore (obrigatório) para Simples */}
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="pro-labore-simples"
                >
                  Pró-labore *
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="pro-labore-simples"
                    placeholder="0,00"
                    type="text"
                    value={formData.proLabore}
                    onChange={(event) =>
                      updateFormField("proLabore", sanitizeBrlValue(event))
                    }
                  />
                </div>
                <small className="text-xs text-gray-600 mt-1 block">
                  Mínimo: R$ 1.518,00 (salário mínimo)
                </small>
              </div>

              {/* Dependentes para Simples */}
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="numero-dependentes-simples"
                >
                  Número de Dependentes
                </label>
                <input
                  className="w-full p-2 border rounded"
                  id="numero-dependentes-simples"
                  placeholder="0"
                  type="number"
                  min="0"
                  max="20"
                  value={formData.numeroDependentes}
                  onChange={(event) =>
                    updateFormField("numeroDependentes", event.target.value)
                  }
                />
                <small className="text-xs text-gray-600 mt-1 block">
                  Para dedução no IRRF sobre o pró-labore
                </small>
              </div>
            </div>
          )}

          {/* Configurações Específicas Lucro Presumido */}
          {formData.regime === "presumido" && (
            <div className="space-y-4">
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="tipo-atividade"
                >
                  Tipo de Atividade *
                </label>
                <select
                  id="tipo-atividade"
                  className="w-full p-2 border rounded"
                  value={formData.ramoAtividade}
                  onChange={(e) =>
                    updateFormField("ramoAtividade", e.target.value)
                  }
                >
                  <option value="">Selecione o tipo</option>
                  <option value="servicos">
                    Prestação de Serviços (~16,33%)
                  </option>
                  <option value="outras">Outras Atividades (~11,33%)</option>
                </select>
              </div>

              {/* Pró-labore (obrigatório) para Presumido */}
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="pro-labore-presumido"
                >
                  Pró-labore *
                </label>
                <div className="flex border">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                    R$
                  </span>
                  <input
                    className="flex-1 p-2 min-w-[100px]"
                    id="pro-labore-presumido"
                    placeholder="0,00"
                    type="text"
                    value={formData.proLabore}
                    onChange={(event) =>
                      updateFormField("proLabore", sanitizeBrlValue(event))
                    }
                  />
                </div>
                <small className="text-xs text-gray-600 mt-1 block">
                  Mínimo: R$ 1.518,00 (salário mínimo)
                </small>
              </div>

              {/* Dependentes para Presumido */}
              <div>
                <label
                  className="block mb-2 font-medium text-sm"
                  htmlFor="numero-dependentes-presumido"
                >
                  Número de Dependentes
                </label>
                <input
                  className="w-full p-2 border rounded"
                  id="numero-dependentes-presumido"
                  placeholder="0"
                  type="number"
                  min="0"
                  max="20"
                  value={formData.numeroDependentes}
                  onChange={(event) =>
                    updateFormField("numeroDependentes", event.target.value)
                  }
                />
                <small className="text-xs text-gray-600 mt-1 block">
                  Para dedução no IRRF sobre o pró-labore
                </small>
              </div>
            </div>
          )}

          {/* Mensagem para quando nenhum regime está selecionado */}
          {!formData.regime && (
            <div className="text-sm text-gray-500 text-center py-4">
              Selecione um regime tributário para ver as configurações
              específicas
            </div>
          )}

          {/* Atualização 2025 */}
          <div className="bg-[#e9ecef] p-4 rounded border">
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              Atualização 2025
            </h3>
            <p className="text-xs text-gray-700">
              Valores DAS MEI, salário mínimo, tabelas IRRF e cálculos do Lucro
              Presumido atualizados conforme legislação 2025.
            </p>
          </div>
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified || isCalculating}
            type="submit"
            variant="default"
            className="sm:w-[150px]"
          >
            {isCalculating ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Calculando...
              </>
            ) : (
              "Calcular"
            )}
          </Button>
          <Button onClick={clear} variant="ghost" type="button">
            Limpar
          </Button>
        </div>
      </form>

      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};
